// ACTIVE screen of the redesigned alarms page (`.context/alarms-redesign/
// spec-active.md`). One full-width row per surfaced alarm — identity, hero
// figure, evidence graphic, actions — then the footer tile strip, which
// persists even when nothing is firing. When the band is empty the ROWS are
// replaced by the all-clear card; the strip stays.
//
// The row IS the state. SEVERITY tints the whole card (emergency + alarm in the
// danger family, warning amber, caution the plain card) and there is no
// left-edge accent stripe anywhere (cloud/web/CLAUDE.md). What the design's
// "no status words" rule cannot absorb is the RUNTIME state: Active, Silenced,
// Acknowledged and AwaitingAcknowledge are four different safety situations and
// an owner has to be able to tell them apart, so `stateMeta` still rides the
// row as a compact chip — amber "Back to normal — tap to acknowledge" is not a
// decoration.
//
// Three honesty rules the cells enforce, all of them inherited rather than new:
//
//   - The hero shows the WHOLE value line (ADR 0170), split into styled pieces
//     that rejoin to the original string. Only the present-tense "Now 12.6 V"
//     shape gets the 40px treatment; a stale or last-transition reading stays
//     at reading size, because size is itself a claim about currency.
//   - The evidence graphic draws only what was measured, and it distinguishes
//     "the window came back empty" from "the read never landed". Only the
//     first earns "No recent data" / "No recent trips"; the second says
//     nothing, because a failed fetch of ours is not a fact about the boat.
//   - Ack / Silence go through the shipped `AckSilenceButtons` unchanged: no
//     optimistic label swap, silence duration named from severity, and
//     `onActionComplete` re-reads the snapshot. This file restyles them in CSS
//     and touches none of their behaviour.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { UnitSettings } from '@neomaris/protocol';

import {
  alarmSeverityLabel,
  formatCountdown,
  isCustomDefinition,
  readWireTrigger,
  resolvedPathFor,
  severityBadgeTone,
  triggerInVocabulary,
  type ActiveAlarmView,
  type AlarmDefView,
} from '../../../lib/alarmDisplay';
import {
  alarmSpecies,
  sparkGeometry,
  type AlarmsFilter,
  type AllClearModel,
  type SafetyOffTile,
  type WatchingTile,
} from '../../../lib/alarmRedesign';
import {
  clearsPhrase,
  convertSeries,
  formatClockTime,
  formatElapsed,
  joinParts,
  nameList,
  readHeroLine,
  scaleSeries,
  tickPositions,
} from '../../../lib/activeScreen';
import { rowUnits } from '../../../lib/alarmLive';
import type { CameraSnapshotRef } from '../../../lib/cameras';
import { convertQuantity, type UnitCategory } from '../../../lib/displayUnits';
import { signalLabel, type Signal } from '../../../lib/signalCatalog';
import { EVIDENCE_HISTORY_MS, type AlarmEvidence } from '../../../lib/useAlarmEvidence';
import { useUnits } from '../../../lib/useUnits';
import { CheckCircleFillIcon, InfoCircleIcon, WarningCircleFillIcon } from '../../app/icons';
import { CameraAlarmSnapshot } from '../../cameras/CameraViews';
import { AckSilenceButtons } from '../AckSilenceButtons';

export interface ActiveScreenProps {
  /** The overview already carries coverage counts beside the incident cards. */
  showSummary?: boolean;
  boatId: string;
  /** The surfaced rows, already sorted most-urgent-then-most-recent. */
  views: readonly ActiveAlarmView[];
  /**
   * Definition by id — the row view carries no trigger, path or custom block,
   * and the redesign's hero caption ("TRIGGER 11.5 V"), species and sparkline
   * threshold all need them. Read through the shared helpers
   * (`alarmSpecies`, `readWireTrigger`, `resolvedPathFor`), never raw.
   */
  definitions: ReadonlyMap<string, AlarmDefView>;
  /** The signal catalog by path — store units, device and channel labels. */
  catalogByPath: ReadonlyMap<string, Signal>;
  /**
   * "Now 12.6 V · alarms below 11.8 V" per definition id (ADR 0170). Absent
   * means the page has no reading it may claim — render the row without a
   * present-tense value rather than substituting one.
   */
  valueLines: ReadonlyMap<string, string>;
  /** 6 h series by path + 24 h firing ticks by alert id. Empty ⇒ no graphic. */
  evidence: AlarmEvidence;
  /**
   * A camera alarm's snapshot by alert id (TOM-797), shown beside its evidence.
   * An alarm with none shows nothing for it.
   */
  cameraSnapshots?: ReadonlyMap<string, CameraSnapshotRef>;
  /** Presence: an unreachable boat disables the relay buttons with a reason. */
  boatOffline: boolean;
  /** The boat's own paused mirror — it changes what the empty state may claim. */
  alarmsPaused: boolean;
  /** Paused / unknown / earned — decided once in `allClearModel`. */
  allClear: AllClearModel;
  /** Footer tile ①: how many alarms the boat is actually evaluating. */
  watching: WatchingTile;
  /** Footer tile ②: safety alarms switched off, with their names. */
  safetyOff: SafetyOffTile;
  /**
   * Footer tile ③: `describeBoatSync(...)`, or `null` when there is nothing to
   * say. The SHELL owns the `alarm-boatsync` testid for this same sentence, so
   * the tile must not repeat that testid.
   */
  boatSyncNote: string | null;
  /** Re-read the alarm snapshot after a relayed ack / silence. */
  onActionComplete: () => void;
  /**
   * A row's EDIT was pressed. The page answers by showing ALL ALARMS with this
   * alarm's inline editor open — the common edit belongs beside the alarm, not
   * on another page. The row title keeps its link to the full editor.
   */
  onEditAlarm: (alarmId: string) => void;
  /** Footer tiles ① and ② deep-link to ALL ALARMS with a filter pre-applied. */
  onNavigateToFilter: (filter: AlarmsFilter) => void;
  /** The page's presence/countdown clock, so every screen ages identically. */
  now: number;
}

/** The sparkline's user-unit box. Drawn with `preserveAspectRatio="none"`. */
const SPARK_BOX = { width: 340, height: 26, padY: 3 } as const;

/** The tick strip's box — 100 wide so a tick's x IS its percentage. */
const STRIP_WIDTH = 100;
const STRIP_HEIGHT = 20;
const STRIP_BASELINE_Y = 16;

function hasCountdown(view: ActiveAlarmView): boolean {
  return view.silenceUntil !== undefined || view.reactivationAt !== undefined;
}

function countdownParts(view: ActiveAlarmView, nowMs: number): string[] {
  const parts: string[] = [];
  if (view.silenceUntil !== undefined) {
    parts.push(`Silenced ${formatCountdown(view.silenceUntil, nowMs)}`);
  }
  if (view.reactivationAt !== undefined) {
    parts.push(`Reactivates ${formatCountdown(view.reactivationAt, nowMs)}`);
  }
  return parts;
}

/** Severity → the row's tint family, through the shared badge mapping. */
function severityToneClass(view: ActiveAlarmView): string {
  switch (severityBadgeTone(view.severity)) {
    case 'danger':
      return 'danger';
    case 'attention':
      return 'warning';
    default:
      return 'neutral';
  }
}

function severityPillClass(view: ActiveAlarmView): string {
  switch (severityBadgeTone(view.severity)) {
    case 'danger':
      return 'al-pill--alarm';
    case 'attention':
      return 'al-pill--warning';
    default:
      return 'al-pill--info';
  }
}

export function ActiveScreen({
  showSummary = true,
  boatId,
  views,
  definitions,
  catalogByPath,
  valueLines,
  evidence,
  cameraSnapshots,
  boatOffline,
  allClear,
  watching,
  safetyOff,
  onActionComplete,
  onNavigateToFilter,
  onEditAlarm,
  now,
}: ActiveScreenProps) {
  // The 1 Hz clock, scheduled ONLY while something on screen actually moves: a
  // live silence/reactivation countdown, or an elapsed hero that is counting in
  // seconds. Nothing firing ⇒ no interval at all. When it is not running the
  // page's own 30 s tick still carries the row, which is why `now` is folded in
  // rather than replaced.
  const needsSecondTick = views.some((view) => {
    if (hasCountdown(view)) return true;
    const def = definitions.get(view.id);
    return def !== undefined && alarmSpecies(def) !== 'analog';
  });
  const [tickMs, setTickMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!needsSecondTick) return;
    const id = window.setInterval(() => setTickMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [needsSecondTick]);
  const nowMs = Math.max(tickMs, now);

  // Tile ①'s sentence. "All quiet" is the same claim the all-clear card has to
  // earn, and it is earned the same way — the boat has reported, and it is not
  // paused. A boat nobody has heard from still has thirteen configured alarms
  // and no news at all, so it gets the count without the reassurance.
  const watchingLead =
    watching.count === 0
      ? 'Nothing else is being watched'
      : [
          `${watching.count} ${watching.count === 1 ? 'alarm' : 'alarms'}`,
          views.length === 0 ? 'watching' : 'more watching',
          allClear.kind === 'clear' ? '— all quiet' : '',
        ]
          .filter((part) => part.length > 0)
          .join(' ');

  return (
    <section className="al-screen al-active" data-testid="alarms-active-band">
      {views.length === 0 ? (
        <AllClearCard model={allClear} />
      ) : (
        views.map((view) => (
          <ActiveRow
            key={view.id}
            view={view}
            definition={definitions.get(view.id)}
            catalogByPath={catalogByPath}
            valueLine={valueLines.get(view.id)}
            evidence={evidence}
            cameraSnapshot={cameraSnapshots?.get(view.id)}
            boatId={boatId}
            boatOffline={boatOffline}
            onActionComplete={onActionComplete}
            onEdit={onEditAlarm}
            nowMs={nowMs}
          />
        ))
      )}

      {/* The strip persists through the all-clear state: "nothing is firing" and
          "this is what is still being watched" are two different reassurances,
          and only the second one is evidence.

          The design's third tile — "Settings not on boat" — is deliberately NOT
          rendered here. It is `describeBoatSync()`, and the page shell already
          gives that exact sentence its own line above the selector (where all
          three screens can see it). Repeating it in the footer would print the
          same claim twice on one screen. */}
      {showSummary ? (
        <div className="al-tiles">
          <button
            type="button"
            className="al-tile al-tile--action"
            onClick={() => onNavigateToFilter('on')}
          >
            <CheckCircleFillIcon
              className={`al-tile__icon ${watching.count === 0 ? 'al-tile__icon--muted' : 'al-tile__icon--healthy'}`}
            />
            <span>
              <span className="al-tile__lead">{watchingLead}</span>
              <span className="al-tile__sub">
                {watching.readingAgoText === null
                  ? 'No readings yet'
                  : `Latest reading ${watching.readingAgoText.toLowerCase()}`}
              </span>
            </span>
          </button>

          {safetyOff.count > 0 ? (
            <button
              type="button"
              className="al-tile al-tile--action al-tile--warning"
              onClick={() => onNavigateToFilter('off')}
            >
              <WarningCircleFillIcon className="al-tile__icon al-tile__icon--warning" />
              <span>
                <span className="al-tile__lead">
                  {safetyOff.count} safety {safetyOff.count === 1 ? 'alarm is' : 'alarms are'} off
                </span>
                <span className="al-tile__sub">{nameList(safetyOff.names) ?? 'Review them'}</span>
              </span>
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The all-clear card.
// ---------------------------------------------------------------------------

/**
 * Four different stories, and only one of them is green. `paused` says nothing
 * is being watched on purpose; `unknown` says the boat has never reported;
 * `idle` says the boat reported and is evaluating nothing at all. None of the
 * three gets the teal check — the check IS the claim, and none has earned it.
 *
 * `alarms-active-empty` sits on the DETAIL line, not the card: two shipped
 * specs read that testid as the whole sentence.
 */
function AllClearCard({ model }: { model: AllClearModel }) {
  const earned = model.kind === 'clear';
  return (
    <div className={`al-allclear${earned ? ' al-allclear--earned' : ''}`}>
      {earned ? (
        <CheckCircleFillIcon className="al-allclear__icon" />
      ) : (
        <InfoCircleIcon className="al-allclear__icon al-allclear__icon--muted" />
      )}
      {model.headline !== null ? <p className="al-allclear__head">{model.headline}</p> : null}
      <p
        className={`al-allclear__detail${earned ? '' : ' al-allclear__detail--prose'}`}
        data-testid="alarms-active-empty"
      >
        {model.detail}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One row.
// ---------------------------------------------------------------------------

interface ActiveRowProps {
  view: ActiveAlarmView;
  definition: AlarmDefView | undefined;
  catalogByPath: ReadonlyMap<string, Signal>;
  valueLine: string | undefined;
  evidence: AlarmEvidence;
  cameraSnapshot: CameraSnapshotRef | undefined;
  boatId: string;
  boatOffline: boolean;
  onActionComplete: () => void;
  onEdit: (alarmId: string) => void;
  nowMs: number;
}

function ActiveRow({
  view,
  definition,
  catalogByPath,
  valueLine,
  evidence,
  cameraSnapshot,
  boatId,
  boatOffline,
  onActionComplete,
  onEdit,
  nowMs,
}: ActiveRowProps) {
  const units = useUnits();
  const species = definition === undefined ? 'none' : alarmSpecies(definition);
  const path = definition === undefined ? null : resolvedPathFor(definition);
  const signal = path === null ? undefined : catalogByPath.get(path);

  // ONE unit decision for the whole row, resolved exactly as `liveValueLine`
  // does — the value line, the clears phrase, the sparkline's threshold and its
  // trace all have to move together or the row contradicts itself. `rowUnits`
  // picks between the two mechanisms that can restate a number here: the
  // recipe's authored domain (hPa → kPa) and the ACCOUNT's unit (ADR 0189),
  // never both.
  const { vocab, category } = rowUnits(signal?.unit, path ?? '', units, {
    rescale: definition !== undefined && !isCustomDefinition(definition),
  });
  const trigger =
    definition === undefined ? null : triggerInVocabulary(readWireTrigger(definition), vocab);

  const tone = severityToneClass(view);
  // The only motion on the page, and it is reserved: an emergency that is
  // ACTIVE right now. A silenced or acknowledged emergency has been seen, and a
  // dot still throbbing at the owner would be nagging, not informing.
  const beaconPulses = view.severity === 'emergency' && view.state === 'Active';

  const sub = joinParts([
    path === null
      ? null
      : (signal?.displayLabel ??
        signalLabel(path, {
          entityName: signal?.entityName,
          deviceName: signal?.deviceName,
          unit: signal?.unit,
          channelLabel: signal?.channelLabel,
        })),
    signal?.deviceName,
    view.occurrence,
  ]);

  const timers = countdownParts(view, nowMs);
  // The same pair the value line beside it was given, so "alarms below 9.8 ft ·
  // clears above 11.5 ft" can never end up half converted.
  const clears = clearsPhrase(trigger, vocab.unit, category, units);

  // Which hero this row leads with. An analog alarm leads with its READING —
  // that is the fact an owner acts on. Everything else leads with how long it
  // has been in this state, because there is no trend to show: a float switch
  // has no value to plot and an absence alarm's value stopped arriving.
  //
  // A definition with no compiled rule (`none`) has no species to speak of, so
  // it takes the reading when the page has one it may claim and falls back to
  // elapsed time when it does not. Either way the value line renders exactly
  // once — in the hero or, below, in the evidence cell.
  const heroIsReading = species === 'analog' || (species === 'none' && valueLine !== undefined);

  return (
    <article className={`al-row al-row--${tone}`} data-testid={`alarm-active-${view.id}`}>
      <div className="al-row__identity">
        <div className="al-row__chips">
          <span
            className={`al-beacon${beaconPulses ? '' : ' al-beacon--static'} al-beacon--${tone}`}
            aria-hidden="true"
          />
          {view.severity ? (
            <span className={`al-pill ${severityPillClass(view)}`}>
              {alarmSeverityLabel(view.severity)}
            </span>
          ) : null}
          <span
            className={`al-statechip alarm-state alarm-state--${view.stateMeta.tone}`}
            data-testid={`alarm-state-${view.id}`}
          >
            {view.stateMeta.label}
          </span>
        </div>
        <Link
          className="al-row__name alarm-row__title"
          to={`/dashboard/boats/${boatId}/alarms/${view.id}`}
          data-testid={`alarm-active-link-${view.id}`}
        >
          {view.title}
        </Link>
        {sub !== null ? <p className="al-row__sub">{sub}</p> : null}
        {view.ackLine ? <p className="al-row__ack">{view.ackLine}</p> : null}
      </div>

      <HeroCell view={view} valueLine={valueLine} nowMs={nowMs} showValueLine={heroIsReading} />

      <div className="al-evidence">
        <CameraAlarmSnapshot boatId={boatId} snapshot={cameraSnapshot} title={view.title} />
        {species === 'analog' ? (
          <AnalogEvidence
            path={path}
            trigger={trigger}
            factor={vocab.factor}
            storeUnit={signal?.unit}
            category={category}
            units={units}
            evidence={evidence}
          />
        ) : null}
        {species === 'binary' ? (
          <TickStripEvidence
            ticks={evidence.ticks.get(view.id)}
            ongoing={view.state === 'Active'}
            nowMs={nowMs}
          />
        ) : null}

        {/* The boat's own sentence about what happened, then what has to happen
            for it to stand down. No trend and no "normally clears in under N" —
            there is no per-alarm baseline anywhere to derive one from, so the
            design's norm phrase is simply not said. */}
        {view.whatHappened !== undefined || clears !== null ? (
          <p className="al-evidence__prose">
            {view.whatHappened !== undefined ? (
              <span className="al-evidence__lead">{view.whatHappened}</span>
            ) : null}
            {clears !== null ? (
              <span className="al-evidence__tail">
                {view.whatHappened !== undefined ? ' · ' : ''}
                {clears}
              </span>
            ) : null}
          </p>
        ) : null}

        {/* A row whose hero is elapsed time still has a reading worth showing
            when the page has one — it belongs here rather than being dropped,
            and it is the same line the ALL ALARMS row shows for this alarm. */}
        {!heroIsReading && valueLine !== undefined ? (
          <p className="al-evidence__value" data-testid={`alarm-active-value-${view.id}`}>
            {valueLine}
          </p>
        ) : null}

        {timers.length > 0 ? (
          <p className="al-evidence__timers" data-testid={`alarm-timers-${view.id}`}>
            {timers.join(' · ')}
          </p>
        ) : null}
      </div>

      <div className="al-row__actions">
        <AckSilenceButtons
          boatId={boatId}
          alertId={view.id}
          state={view.state}
          severity={view.severity}
          boatOffline={boatOffline}
          onActionComplete={onActionComplete}
        />
        {/* The common edit happens where the alarm is READ, so this jumps to
            ALL ALARMS with this row's inline panel already open (`?view=all&
            editing=<id>`) rather than leaving the page for the detail route.
            The row TITLE above still goes to the full editor — that is where
            unlock-to-custom, the signal picker and this alarm's history live. */}
        <button
          type="button"
          className="al-btn al-btn--secondary al-row__edit"
          data-testid={`alarm-active-edit-${view.id}`}
          onClick={() => onEdit(view.id)}
        >
          Edit
        </button>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// The hero cell.
// ---------------------------------------------------------------------------

interface HeroCellProps {
  view: ActiveAlarmView;
  valueLine: string | undefined;
  nowMs: number;
  /** Whether this species' hero is the reading (analog) or elapsed time. */
  showValueLine: boolean;
}

/**
 * Analog rows lead with the READING; every other species leads with how long it
 * has been in this state.
 *
 * The reading hero renders the whole value line — reading and set point — as
 * one element carrying `alarm-active-value-<id>`, split into spans that rejoin
 * to the original string. Both bands are fed by one map and must read
 * identically, so the text stays exactly what the configured row shows; only
 * the TYPOGRAPHY differs, and only for a reading the line itself calls current.
 */
function HeroCell({ view, valueLine, nowMs, showValueLine }: HeroCellProps) {
  if (showValueLine) {
    if (valueLine === undefined) return <div className="al-hero al-hero--empty" />;
    const hero = readHeroLine(valueLine);
    return (
      <div className="al-hero">
        <p className="al-hero__line" data-testid={`alarm-active-value-${view.id}`}>
          {hero.kind === 'now' ? (
            <>
              <span className="al-hero__lead">{hero.lead}</span>
              <span className="al-hero__figure">{hero.value}</span>
              <span className="al-hero__unit">{hero.unit}</span>
              {hero.phrase === null ? null : (
                <span className="al-hero__caption">{hero.phrase}</span>
              )}
            </>
          ) : (
            // Not a present-tense reading: stale, from a past transition, or
            // never reported. It keeps reading size — 40px would say "now".
            <span className="al-hero__reduced">{hero.text}</span>
          )}
        </p>
      </div>
    );
  }

  const elapsed = formatElapsed(view.lastTransitionAt, nowMs);
  const clock = formatClockTime(view.lastTransitionAt);
  if (elapsed === null) return <div className="al-hero al-hero--empty" />;
  return (
    <div className="al-hero">
      <p className="al-hero__line">
        <span className="al-hero__figure">{elapsed.value}</span>
        <span className="al-hero__unit"> {elapsed.unit}</span>
      </p>
      {/* "Since last change", never "firing for": the stamp behind this figure
          is the last STATE change, and an Active → Silenced → Active alarm
          resets it. The occurrence ("3rd time") sits in the identity sub-line
          as the honest companion. */}
      <p className="al-hero__caption al-hero__caption--block">
        {clock === null ? 'since last change' : `since last change ${clock}`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evidence graphics.
// ---------------------------------------------------------------------------

/**
 * The 6 h sparkline: what the signal actually did, against its set point.
 *
 * The trace and the threshold line share one axis, so both numbers take the
 * SAME move — `rowUnits`' documented fork, applied to the pair: an account
 * category (ADR 0189) converts through `convertSeries` / `convertQuantity`,
 * anything else keeps the recipe rescale (`scaleSeries` by the vocabulary's
 * factor, against a set point `triggerInVocabulary` already moved).
 *
 * Nothing on screen moves today: `sparkGeometry` normalizes against the trace's
 * own min/max and the threshold, so a conversion applied to every number cancels
 * out. The fork is here because the pair has to stay convertible TOGETHER — the
 * failure it forecloses is one of them being converted alone, which is what a
 * factor-only path would do the day a °C → °F row needs an axis label.
 */
function AnalogEvidence({
  path,
  trigger,
  factor,
  storeUnit,
  category,
  units,
  evidence,
}: {
  path: string | null;
  trigger: { readonly setpoint?: number } | null;
  factor: number;
  storeUnit: string | null | undefined;
  category: UnitCategory | undefined;
  units: UnitSettings;
  evidence: AlarmEvidence;
}) {
  const series = path === null ? undefined : evidence.series.get(path);
  const raw = series?.points ?? [];
  const points =
    category === undefined
      ? scaleSeries(raw, factor)
      : convertSeries(raw, storeUnit, category, units);
  const setpoint =
    trigger?.setpoint !== undefined && Number.isFinite(trigger.setpoint) ? trigger.setpoint : null;
  const threshold =
    setpoint === null || category === undefined
      ? setpoint
      : convertQuantity(setpoint, storeUnit, category, units).value;
  const geometry = sparkGeometry(points, threshold, SPARK_BOX);

  return (
    <div className="al-graphic">
      <p className="al-microlabel">Last 6 h</p>
      {geometry === null ? (
        // Two different silences, and the map already tells them apart. A key
        // that is PRESENT means the window came back and held nothing — that is
        // "no recent data", a real fact about the signal. A key that is ABSENT
        // means the read has not landed (still in flight, or it failed, or the
        // row has no path to read), and asserting emptiness there would be a
        // claim about the boat drawn from a claim about our own network.
        <p className="al-graphic__none">{series === undefined ? '—' : 'No recent data'}</p>
      ) : (
        <svg
          className="al-spark"
          viewBox={`0 0 ${SPARK_BOX.width} ${SPARK_BOX.height}`}
          preserveAspectRatio="none"
          role="presentation"
        >
          {geometry.thresholdY === null ? null : (
            <line
              className="al-spark__threshold"
              x1={0}
              x2={SPARK_BOX.width}
              y1={geometry.thresholdY}
              y2={geometry.thresholdY}
              vectorEffect="non-scaling-stroke"
            />
          )}
          <polyline
            className="al-spark__trace"
            points={geometry.points}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
    </div>
  );
}

/**
 * The 24 h past-trip strip. Ticks are this ALARM's own firings (history rows
 * whose transition is `Active`), not raw sensor closures — a pump that cycled
 * without tripping shows nothing, which is what "past trips" has to mean.
 */
function TickStripEvidence({
  ticks,
  ongoing,
  nowMs,
}: {
  ticks: number[] | undefined;
  ongoing: boolean;
  nowMs: number;
}) {
  const positions = tickPositions(ticks ?? [], nowMs, EVIDENCE_HISTORY_MS);
  return (
    <div className="al-graphic">
      <p className="al-microlabel">Last 24 h</p>
      {positions.length === 0 && !ongoing ? (
        // Same distinction the sparkline draws. An EMPTY array is a history
        // read that came back with no firings in the window — "no recent
        // trips" is then true. An ABSENT entry is a read that never landed,
        // and for an alarm sitting silenced on this very screen "no recent
        // trips" would contradict the row it is printed inside.
        <p className="al-graphic__none">{ticks === undefined ? '—' : 'No recent trips'}</p>
      ) : (
        <svg
          className="al-strip"
          viewBox={`0 0 ${STRIP_WIDTH} ${STRIP_HEIGHT}`}
          preserveAspectRatio="none"
          role="presentation"
        >
          <line
            className="al-strip__base"
            x1={0}
            x2={STRIP_WIDTH}
            y1={STRIP_BASELINE_Y}
            y2={STRIP_BASELINE_Y}
            vectorEffect="non-scaling-stroke"
          />
          {positions.map((x, i) => (
            <line
              key={`${x}-${i}`}
              className="al-strip__tick"
              x1={x}
              x2={x}
              y1={5}
              y2={STRIP_BASELINE_Y}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* The solid block at the right edge is the CURRENT firing, drawn only
              while the alarm is still Active. */}
          {ongoing ? <rect className="al-strip__now" x={96} y={3} width={4} height={13} /> : null}
        </svg>
      )}
    </div>
  );
}
